/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Button, Card, Col, DatePicker, Flex, Form, Row,
  Select, Space, Statistic, Table, Tag, Typography, message,
} from "antd";
import { SearchOutlined, ClearOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { usageColumns, exportColumns, fmtQty, TYPE_COLOR } from "./model";
import ReportService from "../../../../service/ERP/Report.service";
import OptionService from "../../../../service/ERP/Options.service";
import useOpenDoc from "../../../../hooks/useOpenDoc";
import {
  saveAccessPageStateCookie,
  loadAccessPageStateCookie,
  clearAccessPageStateCookie,
} from "../../../../utils/accessPageStateCookie";

const reportService = ReportService();
const opService = OptionService();
const { RangePicker } = DatePicker;
/* คีย์เก็บเงื่อนไขค้นหาไว้ใน cookie — กลับเข้าหน้านี้อีกครั้งได้ของเดิม */
const PAGE_KEY = "erp_material_usage";

const MaterialUsageAccess = () => {
  const { openJo } = useOpenDoc();
  const [form] = Form.useForm();

  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [searched, setSearched] = useState(false);

  const matType = Form.useWatch("typename", form);

  useEffect(() => {
    reportService
      .materialOptions()
      .then((res) => {
        setMaterials(res.data?.data || []);
        setTypes(res.data?.types || []);
      })
      .catch(() => message.error("โหลดรายการสินค้าไม่สำเร็จ"));

    opService
      .optionsCustomer()
      .then((res) =>
        setCustomers(
          (res.data?.data || []).map((c) => ({
            value: c.cuscode,
            label: `${c.cuscode} — ${c.cusname}`,
            cusname: c.cusname,
          }))
        )
      )
      .catch(() => {});

    /* คืนค่าเงื่อนไขค้นหาที่เคยใช้ แล้วค้นให้เลยถ้ามีสินค้าที่เลือกไว้ */
    const restored = loadAccessPageStateCookie(PAGE_KEY);
    if (restored?.searchValues) {
      form.setFieldsValue(restored.searchValues);
      if (restored.searchValues.mat_code) handleSearch(restored.searchValues);
    }
  }, []);

  /* กรองรายการสินค้าตามประเภทที่เลือกไว้ */
  const materialOptions = useMemo(() => {
    const list = matType ? materials.filter((m) => m.typename === matType) : materials;
    return list.map((m) => ({
      value: m.mat_code,
      label: `${m.mat_code} — ${m.mat_name}  (${m.jo_count} JO)`,
      typename: m.typename,
    }));
  }, [materials, matType]);

  const buildPayload = (v) => {
    const payload = { mat_code: v.mat_code, cuscode: v.cuscode };
    if (v.jodate?.length === 2) {
      payload.jodate_form = dayjs(v.jodate[0]).format("YYYY-MM-DD");
      payload.jodate_to = dayjs(v.jodate[1]).format("YYYY-MM-DD");
    }
    return payload;
  };

  const handleSearch = async (forcedValues = null) => {
    const v = forcedValues ?? (await form.validateFields().catch(() => null));
    if (!v || !v.mat_code) return;

    saveAccessPageStateCookie(PAGE_KEY, { searchValues: v });

    setLoading(true);
    try {
      const res = await reportService.getMaterialUsage(buildPayload(v));
      const d = res.data || {};
      setRows(d.data || []);
      setSummary(d.summary || null);
      setItem(d.item || null);
      setSearched(true);
      if ((d.data || []).length === 0) message.info("ไม่พบการใช้สินค้านี้ในช่วงที่เลือก");
    } catch (err) {
      message.error(err?.response?.data?.message || "ค้นหาไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    clearAccessPageStateCookie(PAGE_KEY);
    form.resetFields();
    setRows([]);
    setSummary(null);
    setItem(null);
    setSearched(false);
  };

  const handleExport = async () => {
    if (rows.length === 0) {
      message.warning("ยังไม่มีข้อมูลให้ export");
      return;
    }
    const v = form.getFieldsValue();
    const dateText =
      v.jodate?.length === 2
        ? `${dayjs(v.jodate[0]).format("DD/MM/YYYY")} - ${dayjs(v.jodate[1]).format("DD/MM/YYYY")}`
        : "ทั้งหมด";
    const cus = customers.find((c) => c.value === v.cuscode);

    setDownloading(true);
    try {
      await reportService.getMaterialUsageDownload({
        data: rows,
        columns: exportColumns(),
        item: item || {},
        filter: { date_text: dateText, cusname: cus?.cusname || "" },
      });
    } catch {
      message.error("Export ไม่สำเร็จ");
    } finally {
      setDownloading(false);
    }
  };

  const FormSearch = (
    <Card>
      <Form form={form} layout="vertical" autoComplete="off">
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} md={5}>
            <Form.Item label="ประเภทสินค้า" name="typename" tooltip="เลือกเพื่อกรองรายการให้แคบลง">
              <Select
                allowClear
                placeholder="ทุกประเภท"
                options={types.map((t) => ({
                  value: t,
                  label: <Tag color={TYPE_COLOR[t] || "default"}>{t}</Tag>,
                }))}
                onChange={() => form.setFieldValue("mat_code", undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item
              label="สินค้าที่ต้องการดู"
              name="mat_code"
              tooltip="แสดงเฉพาะรายการที่เคยถูกใช้ใน Job Order จริง — วงเล็บคือจำนวนใบ JO ที่เคยใช้"
              rules={[{ required: true, message: "กรุณาเลือกสินค้า" }]}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="ค้นหารหัส / ชื่อสินค้า"
                options={materialOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="ช่วงวันที่ JO" name="jodate">
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={["ตั้งแต่วันที่", "ถึงวันที่"]}
                style={{ width: "100%", height: 40 }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Form.Item label="ลูกค้า" name="cuscode">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="ทุกราย"
                options={customers}
              />
            </Form.Item>
          </Col>
        </Row>

        <Flex justify="flex-end" gap={8}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            onClick={() => handleSearch()}
          >
            ค้นหา
          </Button>
          <Button danger icon={<ClearOutlined />} onClick={handleClear}>
            ล้าง
          </Button>
        </Flex>
      </Form>
    </Card>
  );

  const SummaryBar = summary && item && (
    <Card>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={10}>
          <Space direction="vertical" size={2}>
            <Space wrap>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {item.mat_code}
              </Typography.Text>
              {item.typename && (
                <Tag color={TYPE_COLOR[item.typename] || "default"}>{item.typename}</Tag>
              )}
            </Space>
            <Typography.Text type="secondary">{item.mat_name}</Typography.Text>
          </Space>
        </Col>
        <Col xs={12} md={5}>
          <Statistic title="จำนวนที่ใช้รวม" value={fmtQty(summary.total_qty)} suffix={summary.unit} />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="จำนวน Job Order" value={summary.job_count} suffix="ใบ" />
        </Col>
        <Col xs={24} md={5}>
          <Flex justify="flex-end">
            <Button
              icon={<FileExcelOutlined />}
              loading={downloading}
              onClick={handleExport}
              style={{ borderColor: "#237804", color: "#237804" }}
            >
              Export Excel
            </Button>
          </Flex>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="material-usage">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Typography.Title level={3} className="m-0 !text-zinc-800">
          รายงานการใช้ส่วนผสมตาม Job Order
        </Typography.Title>

        {FormSearch}
        {SummaryBar}

        {searched && (
          <Card>
            <Table
              size="small"
              rowKey={(r) => `${r.jocode}-${r.detail_code}-${r.seq}`}
              loading={loading}
              columns={usageColumns({ openJo })}
              dataSource={rows}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 50, showSizeChanger: true }}
              bordered
              summary={(data) => {
                const total = data.reduce((s, r) => s + Number(r.qty || 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={8} align="right">
                        <Typography.Text strong>รวมในหน้านี้</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <Typography.Text strong>{fmtQty(total)}</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} colSpan={2} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        )}
      </Space>
    </div>
  );
};

export default MaterialUsageAccess;
